import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { ClockIcon } from '@heroicons/react/outline';
import {
    add,
    eachDayOfInterval,
    endOfMonth,
    format,
    getDay,
    isEqual,
    isSameDay,
    isSameMonth,
    isToday,
    parse,
    parseISO,
    startOfToday,
    setHours,
    setMinutes
} from 'date-fns';
import FormDatePicker from '../../../../common/components/formElements/FormDatePicker';
import es from 'date-fns/locale/es';
import { Dialog, Transition } from '@headlessui/react';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Appointment } from '../../../services/Appointment';
import { useTable, usePagination, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import SimpleDataTable from '../../../../doctorAppointmentManager/components/appointmentHistory/components/simpleDataTable/SimpleDataTable';
import Today from '../../../components/Today';
import DeleteConfirmationModal from '../../../../common/components/deleteConfirmationModal/DeleteConfirmationModal';
import CustomSuccessModal from '../../../../common/components/customSuccessModal/CustomSuccessModal';

const formSchema = z.object({
    reason: z.string({ required_error: 'Requerido' }).nonempty({ message: 'Requerido' }),
    startTime: z.date().optional(),
    endTime: z
        .date()
        .refine(
            (value, data) => {
                if (data?.startTime === undefined) {
                    return true; // No comparison needed when startTime is undefined
                }
                return value >= data?.startTime;
            },
            { message: 'Las horas de inicio deben ser posteriores o iguales a las horas de cierre', path: ['endTime'] }
        )
        .optional()
});

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

let colStartClasses = ['', 'col-start-2', 'col-start-3', 'col-start-4', 'col-start-5', 'col-start-6', 'col-start-7'];

const DaySelected = ({ selectedDay }) => (
    <h2 className="font-bold">{'Día seleccionado  ' + format(selectedDay, 'MMM dd, yyy')}</h2>
);

const BlockSlot = () => {
    const navigate = useNavigate();
    const [alert, setAlert] = useState(false);
    const [open, setIsOpen] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [editData, setEditData] = useState({ id: '', startTime: '', endTime: '', reason: '' });

    const { register, handleSubmit, formState, control, getValues, reset } = useForm({
        resolver: zodResolver(formSchema)
    });
    const { errors } = formState;
    const onCloseSuccessMessage = () => setShowSuccessModal(false);
    let today = startOfToday();
    let [selectedDay, setSelectedDay] = useState(today);
    let [currentMonth, setCurrentMonth] = useState(format(today, 'MMM-yyyy'));
    let firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date());
    let days = eachDayOfInterval({ start: firstDayCurrentMonth, end: endOfMonth(firstDayCurrentMonth) });

    function previousMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
        setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
    }

    function nextMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
        setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
    }

    let selectedDayAppointments = [];

    const saveForm = (values) => {
        values.startTime = values.startTime ? format(values.startTime, 'HH:mm') : undefined;
        values.endTime = values.endTime ? format(values.endTime, 'HH:mm') : undefined;
        Appointment.blockSlot({ ...values, date: format(selectedDay, 'yyyy-MM-dd') })
            .then(() => {
                setAlert(true);
                setTimeout(() => {
                    setAlert(false);
                }, 3000);
            })
            .catch((err) => console.log(err));
    };

    const [allblockSlot, setSlots] = useState([]);

    useEffect(() => {
        Appointment.allblockSlot()
            .then((res) => setSlots(res.data))
            .catch((e) => console.log(e));
    }, []);
    const handleEdit = (slot) => {
        setEditData(slot);

        setIsOpen(true);
        reset({
            reason: slot.col2,
            startTime: parse(slot.col3, 'HH:mm', new Date()),
            endTime: parse(slot.col4, 'HH:mm', new Date())
        });
    };

    const slotsColumns = useMemo(() => {
        return [
            { Header: 'Día', accessor: 'col1' },
            { Header: 'Razón', accessor: 'col2' },
            { Header: 'Hora de inicio', accessor: 'col3' },
            { Header: 'Hora final', accessor: 'col4' },
            {
                Header: 'Acciones',
                id: 'actions',
                Cell: ({ row }) => (
                    <div className="flex justify-center gap-2">
                        <button
                            style={{ color: '#00a9f6' }}
                            onClick={() => handleEdit(row.original)}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                            Editar
                        </button>
                        <button
                            style={{ color: '#de4229' }}
                            onClick={() => handleDelete(row)}
                            className="text-red-600 hover:text-red-800 font-medium"
                        >
                            Borrar
                        </button>
                    </div>
                )
            }
        ];
    }, []);

    const completedTable = useMemo(() => {
        return allblockSlot.map((slot) => {
            return {
                col1: slot.date, // date
                col2: slot.reason, // reason
                col3: slot.startTime, // startTime
                col4: slot.endTime, // endTime
                id: slot.id
            };
        });
    }, [allblockSlot]);

    const completedTableInstance = useTable(
        { columns: slotsColumns, data: completedTable },
        useFilters, // useFilters!
        useGlobalFilter, // useGlobalFilter!,
        useSortBy,
        usePagination
    );
    const handleDelete = async (id) => {
        
        await Appointment.deleteBlockSlot(id.original.id);
        setSlots((slots) => slots.filter((slot) => slot.id !== id.original.id));
        setOpenDeleteModal(false);
    };

    const handleUpdate = (data) => {
        Appointment.updateBlockSlot({ ...data, id: editData.id })
            .then(() => {
                setAlert(true);
                setTimeout(() => {
                    setAlert(false);
                }, 3000);
                setIsOpen(false);
                reset();
                Appointment.allblockSlot()
                    .then((res) => setSlots(res.data))
                    .catch((e) => console.log(e));
            })
            .catch((err) => console.log(err));
           
    };
  
    return (
        <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-10 side-bar-right bg-dl-screen-lightblue">
            {/* calendar */}
            <div>
                <Today />
            </div>
            <form onSubmit={handleSubmit(saveForm)}>
                <div className="bg-white calender-box box-radious mb-4">
                    <div className="md:grid md:grid-cols-2 md:divide-x md:divide-gray-200">
                        <div className="md:pr-14 ">
                            <div className="flex items-center calender-top-head">
                                <h2 className="flex-auto font-bold text-gray-900 capitalize calender-title mb-0">
                                    {format(firstDayCurrentMonth, 'MMMM yyyy', { locale: es })}
                                </h2>
                                <button
                                    type="button"
                                    onClick={previousMonth}
                                    className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                                >
                                    <span className="sr-only">Previous month</span>
                                    <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    onClick={nextMonth}
                                    className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                                >
                                    <span className="sr-only">Next month</span>
                                    <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
                                </button>
                            </div>
                            <div className="grid grid-cols-7 mt-10 text-xl leading-6 text-center text-gray-500 week-name-list">
                                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((item, i) => (
                                    <div key={i}>{item}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 mt-2 text-xl">
                                {days.map((day, dayIdx) => (
                                    <div
                                        key={day.toString()}
                                        className={classNames(dayIdx === 0 && colStartClasses[getDay(day)], 'py-1.5')}
                                    >
                                        <button
                                            type="button"
                                            disabled={day < today}
                                            onClick={() => {
                                                setSelectedDay(day);
                                                reset({ startTime: undefined, endTime: undefined });
                                                //   setShowDetail(false);
                                            }}
                                            className={classNames(
                                                isEqual(day, selectedDay) && 'text-white',
                                                !isEqual(day, selectedDay) && isToday(day) && 'text-dl-red',
                                                !isEqual(day, selectedDay) &&
                                                    !isToday(day) &&
                                                    isSameMonth(day, firstDayCurrentMonth) &&
                                                    'text-gray-900',
                                                !isEqual(day, selectedDay) &&
                                                    !isToday(day) &&
                                                    !isSameMonth(day, firstDayCurrentMonth) &&
                                                    'text-gray-400',
                                                isEqual(day, selectedDay) && isToday(day) && 'bg-dl-red',
                                                isEqual(day, selectedDay) && !isToday(day) && 'bg-dl-accent',
                                                !isEqual(day, selectedDay) && 'hover:bg-gray-200',
                                                (isEqual(day, selectedDay) || isToday(day)) && 'font-semibold',
                                                day < today && 'cursor-not-allowed text-gray-300 text-lg	',
                                                'mx-auto flex h-8 w-8 items-center justify-center rounded-full active-btn-number'
                                            )}
                                        >
                                            <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                                        </button>

                                        <div className="w-2 h-2 mx-auto mt-1">
                                            {selectedDayAppointments.some((appointment) =>
                                                isSameDay(parseISO(appointment.appDate), day)
                                            ) && <div className="w-2 h-2 rounded-full bg-sky-500"></div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <section className="mt-12 md:mt-0 calender-time-box pl-10">
                            <div className="mb-4">
                                <DaySelected selectedDay={selectedDay} />
                            </div>
                            <div className="grid grid-cols-12 gap-6 mb-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Controller
                                        control={control}
                                        name="startTime"
                                        //   rules={{ required: true }}
                                        render={({ field }) => {
                                            return (
                                                <FormDatePicker
                                                    identifier="startTime"
                                                    label="Seleccionar hora de inicio "
                                                    errorMessage={errors?.startTime ? 'Este campo es requerido' : null}
                                                    iconComponent={
                                                        <ClockIcon
                                                            className="h-5 w-5 text-gray-400"
                                                            aria-hidden="true"
                                                        />
                                                    }
                                                    datePickerProps={{
                                                        onKeyDown: (e) => {
                                                            e.preventDefault();
                                                        },
                                                        selected: field.value,
                                                        dateFormat: 'HH:mm:ss',
                                                        onChange: (date) => field.onChange(date),
                                                        showTimeSelect: true,
                                                        showTimeSelectOnly: true,
                                                        timeIntervals: 15,
                                                        timeCaption: 'Hora',
                                                        dropdownMode: 'select',
                                                        minTime: setHours(setMinutes(new Date(), 0), 7),
                                                        maxTime: setHours(setMinutes(new Date(), 0), 20)
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                </div>

                                <div className="col-span-12 md:col-span-6">
                                    <Controller
                                        control={control}
                                        name="endTime"
                                        render={({ field }) => (
                                            <FormDatePicker
                                                identifier="endTime"
                                                label="Seleccionar hora final"
                                                errorMessage={errors?.endTime?.message}
                                                iconComponent={
                                                    <ClockIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                                }
                                                datePickerProps={{
                                                    onKeyDown: (e) => {
                                                        e.preventDefault();
                                                    },
                                                    selected: field.value,
                                                    dateFormat: 'HH:mm:ss',
                                                    onChange: (date) => field.onChange(date),
                                                    showTimeSelect: true,
                                                    showTimeSelectOnly: true,
                                                    timeIntervals: 15,
                                                    timeCaption: 'Hora',
                                                    dropdownMode: 'select',
                                                    minTime: setHours(setMinutes(new Date(), 0), 7),
                                                    maxTime: setHours(setMinutes(new Date(), 0), 20)
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="">
                                <h4 className="title-info">Razón</h4>
                                <textarea
                                    {...register('reason')}
                                    className=" w-full h-24 resize-none font-normal text-sm border border-gray-300 rounded-xl"
                                    placeholder="Ingrese el motivo para bloquear el espacio"
                                ></textarea>
                                <span className="required">*Requerido</span>
                            </div>
                        </section>
                    </div>
                </div>

                {alert ? (
                    <div className="flex items-center justify-center mb-2">
                        <div className="green-info-box w-1/2">
                            <h5>Blocked slot:</h5>
                            <DaySelected selectedDay={selectedDay} />
                        </div>
                    </div>
                ) : null}

                <div className="flex justify-center mt-20">
                    <button type="submit" className="btn btn-primary btn-sm">
                        Bloquear espacio
                    </button>
                </div>
            </form>

            {/* table */}

            {allblockSlot.length ? <SimpleDataTable tableInstance={completedTableInstance} /> : null}
            <Transition.Root show={open} as={Fragment}>
                <Dialog
                    onClose={() => {
                        setIsOpen(false);
                        reset();
                    }}
                    className="fixed inset-0 z-10 overflow-y-auto"
                >
                    <div className="flex min-h-screen items-center justify-center p-4 text-center">
                        <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                Editar Bloqueo
                            </Dialog.Title>
                            <form
                                onSubmit={handleSubmit((data) => {
                                    handleUpdate({
                                        ...data,
                                        startTime: format(data.startTime, 'HH:mm'),
                                        endTime: format(data.endTime, 'HH:mm'),
                                        date: format(selectedDay, 'yyyy-MM-dd')
                                    });
                                })}
                                className="mt-4 space-y-4"
                            >
                                <Controller
                                    control={control}
                                    name="startTime"
                                    render={({ field }) => (
                                        <FormDatePicker
                                            identifier="edit-startTime"
                                            label="Hora de inicio"
                                            iconComponent={<ClockIcon className="h-5 w-5 text-gray-400" />}
                                            datePickerProps={{
                                                onKeyDown: (e) => e.preventDefault(),
                                                selected: field.value,
                                                onChange: (date) => field.onChange(date),
                                                showTimeSelect: true,
                                                showTimeSelectOnly: true,
                                                timeIntervals: 15,
                                                dateFormat: 'HH:mm:ss',
                                                timeCaption: 'Hora',
                                                minTime: setHours(setMinutes(new Date(), 0), 7),
                                                maxTime: setHours(setMinutes(new Date(), 0), 20)
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    control={control}
                                    name="endTime"
                                    render={({ field }) => (
                                        <FormDatePicker
                                            identifier="edit-endTime"
                                            label="Hora final"
                                            iconComponent={<ClockIcon className="h-5 w-5 text-gray-400" />}
                                            datePickerProps={{
                                                onKeyDown: (e) => e.preventDefault(),
                                                selected: field.value,
                                                onChange: (date) => field.onChange(date),
                                                showTimeSelect: true,
                                                showTimeSelectOnly: true,
                                                timeIntervals: 15,
                                                dateFormat: 'HH:mm:ss',
                                                timeCaption: 'Hora',
                                                minTime: setHours(setMinutes(new Date(), 0), 7),
                                                maxTime: setHours(setMinutes(new Date(), 0), 20)
                                            }}
                                        />
                                    )}
                                />

                                <div>
                                    <label htmlFor="edit-reason" className="block text-sm font-medium text-gray-700">
                                        Razón
                                    </label>
                                    <input
                                        id="edit-reason"
                                        {...register('reason')}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    />
                                </div>

                                <div className="mt-4 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="bg-dl-primary-700 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-primary-500 disabled:bg-dl-primary-500"
                                        onClick={() => {
                                            setIsOpen(false);
                                            reset();
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-dl-primary-700 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-primary-500 disabled:bg-dl-primary-500"
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </form>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            </Transition.Root>
            {showSuccessModal && (
                <CustomSuccessModal
                    // title={successTitle}
                    bodyText="Bloqueo creado exitosamente"
                    buttonText="Aceptar"
                    buttonAction={onCloseSuccessMessage}
                    open={true}
                />
            )}
            {openDeleteModal && (
                <DeleteConfirmationModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    onCancel={() => setOpenDeleteModal(false)}
                    onConfirm={() => handleDelete()}
                    title="Are you sure you want to delete?"
                    bodyText="This action cannot be undone. Please confirm you want to delete this item."
                />
            )}
        </div>
    );
};

export default BlockSlot;
